import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  MapPin, 
  Heart
} from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import image1 from '../assets/images/image1.png';
import image2 from '../assets/images/image2.png';
import image3 from '../assets/images/image3.png';

const HeroSection: React.FC = () => {
  const { t } = useLanguageStore();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTab, setActiveTab] = useState<'mission' | 'vision'>('mission');

  const heroSlides = [
    {
      title: 'Excellence in Healthcare',
      titleSinhala: 'සෞඛ්‍ය සේවයේ විශිෂ්ටත්වය',
      subtitle: 'Providing compassionate care for our community',
      subtitleSinhala: 'අපගේ ප්‍රජාව සඳහා දයානුකම්පිත සත්කාර',
      bgImage: `url(${image1})`
    },
    {
      title: 'Advanced Medical Care',
      titleSinhala: 'උසස් වෛද්‍ය සත්කාර',
      subtitle: 'Modern facilities with traditional Sri Lankan hospitality',
      subtitleSinhala: 'සාම්ප්‍රදායික ශ්‍රී ලාංකික ආගන්තුක සත්කාරය සහිත නවීන පහසුකම්',
      bgImage: `url(${image2})`
    },
    {
      title: '24/7 Emergency Services',
      titleSinhala: '24/7 හදිසි සේවාවන්',
      subtitle: 'Always here when you need us most',
      subtitleSinhala: 'ඔබට අපට අවශ්‍යම වේලාවේ අපි සෑම විටම මෙහි සිටිමු',
      bgImage: `url(${image3})`
    }
  ];

  const quickActions = [
    {
      icon: Search,
      label: 'Channel a Doctor',
      labelSinhala: 'වෛද්‍යවරයකු චැනල් කරන්න',
      href: '/doctors'
    },
    {
      icon: MapPin,
      label: 'Directions',
      labelSinhala: 'දිශානති',
      href: '/location'
    }
  ];


  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const currentHero = heroSlides[currentSlide];

  return (
    <section className="relative min-h-[600px] md:min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with Sri Lankan Pattern */}
      <div className="absolute inset-0 sri-pattern">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
          style={{ 
            background: currentHero.bgImage,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
                />
                {/* Light overlay for better text readability */}
                <div className="absolute inset-0 bg-white/40" />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-20 h-20 opacity-10"
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              rotate: [0, 360],
            }}
            transition={{
              duration: 6 + i,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + i * 12}%`,
            }}
          >
            <Heart className="w-full h-full text-primary-green" />
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.h1
              key={`title-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
                    className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight text-text-dark"
                    style={{ textShadow: '1px 1px 3px rgba(255,255,255,0.8), 0 0 10px rgba(255,255,255,0.5)' }}
            >
              {t(`hero-title-${currentSlide}`, currentHero.title, currentHero.titleSinhala)}
            </motion.h1>

            <motion.p
              key={`subtitle-${currentSlide}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
                    className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-text-secondary font-medium"
                    style={{ textShadow: '1px 1px 2px rgba(255,255,255,0.8), 0 0 8px rgba(255,255,255,0.5)' }}
            >
              {t(`hero-subtitle-${currentSlide}`, currentHero.subtitle, currentHero.subtitleSinhala)}
            </motion.p>


            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-2 gap-3 sm:gap-4"
            >
              {quickActions.map((action, index) => (
                <motion.a
                  key={action.label}
                  href={action.href}
                        className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl p-3 sm:p-4 text-center hover:bg-white/90 transition-all duration-300 group shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                        <action.icon className="w-6 h-6 sm:w-8 sm:h-8 text-primary-green mx-auto mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" />
                        <span className="text-xs sm:text-sm font-medium text-text-dark">
                    {t(`quick-${action.label.toLowerCase().replace(/\s+/g, '-')}`, action.label, action.labelSinhala)}
                  </span>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Creative Mission */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden md:block"
          >
                  <div 
                    className="relative bg-gradient-to-br from-white/80 via-white/70 to-white/60 rounded-3xl p-6 md:p-8 shadow-2xl overflow-hidden border border-white/40"
                    style={{ 
                      backdropFilter: 'blur(12px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)'
                    }}
                  >
              {/* Decorative Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                      <div className="absolute top-4 right-4 w-20 h-20 border-2 border-primary-green/20 rounded-full"></div>
                      <div className="absolute bottom-6 left-6 w-16 h-16 border border-secondary-blue/20 rounded-lg rotate-45"></div>
                      <div className="absolute top-1/2 left-4 w-8 h-8 bg-accent-gold/20 rounded-full"></div>
              </div>

              {/* Tab Switcher with ARIA attributes */}
              <div 
                className="flex gap-2 mb-6 justify-center relative z-10" 
                role="tablist"
                aria-label="Mission and Vision tabs"
              >
                <button
                  onClick={() => setActiveTab('mission')}
                  role="tab"
                  aria-selected={activeTab === 'mission'}
                  aria-controls="mission-panel"
                  id="mission-tab"
                  tabIndex={activeTab === 'mission' ? 0 : -1}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === 'mission'
                      ? 'bg-gradient-to-r from-accent-gold to-primary-green text-white shadow-lg'
                      : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
                  }`}
                >
                  {t('mission', 'MISSION', 'මෙහෙවර')}
                </button>
                <button
                  onClick={() => setActiveTab('vision')}
                  role="tab"
                  aria-selected={activeTab === 'vision'}
                  aria-controls="vision-panel"
                  id="vision-tab"
                  tabIndex={activeTab === 'vision' ? 0 : -1}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    activeTab === 'vision'
                      ? 'bg-gradient-to-r from-accent-gold to-primary-green text-white shadow-lg'
                      : 'bg-gray-100 text-text-secondary hover:bg-gray-200'
                  }`}
                >
                  {t('vision', 'VISION', 'අරමුණ')}
                </button>
              </div>

              {/* Mission/Vision Header with Icon */}
              <motion.div
                key={activeTab}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8 relative z-10"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-accent-gold to-primary-green rounded-2xl flex items-center justify-center shadow-lg">
                  <motion.div
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Heart className="w-8 h-8 text-white" />
                  </motion.div>
                </div>
                <h3 className="text-2xl font-bold text-text-dark mb-2 tracking-wider">
                  {activeTab === 'mission' 
                    ? t('mission', 'MISSION', 'මෙහෙවර')
                    : t('vision', 'VISION', 'අරමුණ')
                  }
                </h3>
                <div className="w-16 h-1 bg-gradient-to-r from-accent-gold via-white to-primary-green mx-auto rounded-full"></div>
              </motion.div>

              {/* Mission/Vision Statement with Enhanced Design and ARIA */}
              <motion.div
                key={`content-${activeTab}`}
                initial={{ opacity: 0, x: activeTab === 'mission' ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: activeTab === 'mission' ? -20 : 20 }}
                transition={{ duration: 0.5 }}
                className="relative"
                role="tabpanel"
                id={`${activeTab}-panel`}
                aria-labelledby={`${activeTab}-tab`}
              >
                {/* Quote-like Design */}
                <div className="relative bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 shadow-lg">
                  {/* Quote Mark */}
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-accent-gold rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    "
                  </div>
                  
                  {/* Mission/Vision Text */}
                  <motion.p
                    className="text-text-dark leading-relaxed text-lg font-medium text-center italic"
                    style={{ textShadow: 'none' }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                  >
                    {activeTab === 'mission' ? (
                      t(
                        'mission-statement',
                        'Creating a satisfied customer through quality, efficient health services.',
                        'ගුණාත්මක කාර්යක්ෂම සෞඛ්‍ය සේවාවක් තුලින් තෘප්තිමත් සේවාලාභියෙකු බිහිකිරීම.'
                      )
                    ) : (
                      <>
                        <div className="mb-4">
                          {t(
                            'vision-statement-en',
                            'Lifting up the Hospital its Utmost, Throughout The best in the island Creating a Healthy Tomorrow for the Generation of Human in Ruhunu Giruwapattu.',
                            ''
                          )}
                        </div>
                        <div className="text-base">
                          {t(
                            'vision-statement-si',
                            '',
                            'රුහුණු ගිරුවාපත්තුවේ සැමට සුවදායී හෙටක් උදාකිරීම තුලින් දිවවිනේ විශිෂ්ඨතම දිසා රෝහල බවට පත් කිරීම.'
                          )}
                        </div>
                      </>
                    )}
                  </motion.p>

                  {/* Closing Quote Mark */}
                  <div className="absolute -bottom-3 -right-3 w-8 h-8 bg-primary-green rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    "
                  </div>
                </div>

                {/* Values Indicators */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.8, duration: 0.6 }}
                  className="flex justify-center gap-4 mt-6"
                >
                        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                          <div className="w-2 h-2 bg-accent-gold rounded-full animate-pulse"></div>
                          <span className="text-text-secondary text-sm font-medium">
                      {t('quality', 'Quality', 'ගුණාත්මක')}
                    </span>
                  </div>
                        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                          <div className="w-2 h-2 bg-primary-green rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                          <span className="text-text-secondary text-sm font-medium">
                      {t('care', 'Care', 'සත්කාර')}
                    </span>
                  </div>
                        <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm">
                          <div className="w-2 h-2 bg-secondary-blue rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                          <span className="text-text-secondary text-sm font-medium">
                      {t('service', 'Service', 'සේවාව')}
                    </span>
                  </div>
                </motion.div>
              </motion.div>

              {/* Floating Animation Elements */}
              <motion.div
                className="absolute top-6 right-8 w-3 h-3 bg-accent-gold rounded-full"
                animate={{
                  y: [-5, 5, -5],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute bottom-8 left-8 w-2 h-2 bg-primary-green rounded-full"
                animate={{
                  x: [-3, 3, -3],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Slide Indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 md:gap-3"
        >
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-text-dark scale-125' 
                  : 'bg-text-dark/50 hover:bg-text-dark/75'
              }`}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
